import type { CombinedGame, Game, PlaygameMessage } from '@playgame/types'
import type { GameProviderSlug } from '@game/types'
import { useState, createSharedComposable, useCookie, usePlaygameView, computed, useGameType, readonly } from '#imports'
import ProviderService from '@playgame/utils/ProviderService'

export const usePlaygameState = createSharedComposable(() => {
    const chatCookie = useCookie<boolean>('chatCookie')

    const { isMobileView } = usePlaygameView()
    const { isBingo } = useGameType()

    // Writable flags — PRIVATE. Mutated only by the state-action layer via
    // `_writableState`; public consumers get the deeply-readonly `state` view.
    const flags = useState('playgame', () => ({
        sound: true,
        fullScreen: false,
        chat: isMobileView.value ? false : chatCookie.value,
        chatMinimized: false,
        paymentsVisible: false,
        menuVisible: false,
        balanceDropdownVisible: false,
        canLoadChat: false,
        gameHasFreeSpins: false,
        gameStarting: false,
        focusedGame: ''
    }))

    // Games live outside the readonly flags object so Game instances stay mutable;
    // the Map itself is mutated only via addGame/removeGameFromState.
    const games = useState('playgame-games', () => new Map<string, Game>())

    // Found by slug but not launched yet, so the view can show its loader during the launch.
    const startingGame = useState<CombinedGame | null>('playgame-starting-game', () => null)

    // Writable message — PRIVATE. Mutated only by usePlaygameMessage via `_writableMessage`.
    const messageRaw = useState<PlaygameMessage | null>('playgame-message', () => null)

    const getGame = (identifier: string) => games.value.get(identifier)

    const removeGameFromState = (identifier: string) => {
        if (getGame(identifier)) {
            games.value.delete(identifier)
        }
    }

    const isDialogOpen = computed<boolean>(() => flags.value.paymentsVisible || flags.value.menuVisible)
    const gamesArray = computed(() => Array.from(games.value.values()))
    const getFocusedGame = computed(() => flags.value.focusedGame ? games.value.get(flags.value.focusedGame) : null)

    const getEvolutionGames = computed(() =>
        gamesArray.value.filter(game => ProviderService.isEvolution(game.providerId))
    )
    const getGamesGlobalGames = computed(() =>
        gamesArray.value.filter(game => ProviderService.isGamesGlobal(game.providerId))
    )
    const getMagnetGames = computed(() =>
        gamesArray.value.filter(game => ProviderService.isMagnet(game.providerId))
    )
    const getBingoGames = computed(() =>
        gamesArray.value.filter(game => isBingo(game.game_type))
    )
    const getPariplayGames = computed(() =>
        gamesArray.value.filter(game => ProviderService.isPariplay(game.providerId))
    )

    const isPlayingEvolutionGames = computed<boolean>(() => getEvolutionGames.value.length > 0)
    const isPlayingGamesGlobalGames = computed<boolean>(() => getGamesGlobalGames.value.length > 0)
    const isPlayingBingo = computed<boolean>(() => getBingoGames.value.length > 0)
    const isPlayingPariplayGames = computed<boolean>(() => getPariplayGames.value.length > 0)

    const isPlayingGameAlready = (slug: string): boolean => {
        return gamesArray.value.some(game => game.combinedGame.slug === slug)
    }

    const isOneAtATimeRestricted = (gameType: string | undefined, provider: number | GameProviderSlug | undefined, slug?: string): boolean => {
        if (isBingo(gameType)) {
            return slug ? isPlayingGameAlready(slug) : false
        }

        return (ProviderService.isEvolution(provider) && isPlayingEvolutionGames.value)
            || (ProviderService.isGamesGlobal(provider) && isPlayingGamesGlobalGames.value)
            || (ProviderService.isPariplay(provider) && isPlayingPariplayGames.value)
    }

    // Public, deeply-readonly views. Mutate only through state actions.
    const state = readonly(flags)
    const message = readonly(messageRaw)

    return {
        state,
        message,
        games,
        startingGame,
        gamesArray,
        chatCookie,
        getGame,
        removeGameFromState,
        getFocusedGame,
        getEvolutionGames,
        getGamesGlobalGames,
        getMagnetGames,
        isPlayingEvolutionGames,
        isPlayingGamesGlobalGames,
        isPlayingPariplayGames,
        isPlayingBingo,
        isDialogOpen,
        isPlayingGameAlready,
        isOneAtATimeRestricted,
        // Internal writable handles — state-action layer ONLY (useStateActions,
        // useGameManager, usePlaygameMessage); components must use exported actions.
        _writableState: flags,
        _writableMessage: messageRaw
    }
})
